import { useCallback, useMemo } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import type {
  ConfigEntityDetail,
  EntityBulkOperationStatus,
  EntityDependencyRef,
  EnvironmentKey,
} from '@web-config-app/core';
import {
  copyEntitiesToEnvironmentDB as db,
  topologicalSortEntities,
} from '@web-config-app/core';

// Bind singleton methods once for reuse across all hook instances
const getEntityEntryById = db.getEntityEntryById.bind(db);

/**
 * Status of a single dependency
 */
export interface EntityDependencyStatus {
  /** The dependency reference (id and domainEntityType) */
  dependency: EntityDependencyRef;
  /** The current status of the dependency, or undefined if not found in the database */
  status: EntityBulkOperationStatus | undefined;
  /** Whether this dependency is satisfied (complete or skipped) */
  isSatisfied: boolean;
}

/**
 * Result of querying an entity's dependencies status
 */
export interface EntityDependenciesStatusResult {
  /** Status of each individual dependency */
  dependencies: EntityDependencyStatus[];
  /** Whether all dependencies are satisfied (complete or skipped) */
  allSatisfied: boolean;
  /** Count of dependencies by status */
  counts: {
    ready: number;
    complete: number;
    error: number;
    skipped: number;
  };
}

/**
 * A React hook that provides an interface for managing entities in IndexedDB
 * for copy to environment operations.
 *
 * This hook offers reactive queries and CRUD operations for entities stored in IndexedDB.
 * It uses Dexie's live query functionality to automatically update when the underlying data changes.
 *
 * @returns An object containing reactive data and methods for entity management:
 * - `entities`: Live-updating array of entity entries (or undefined while loading)
 * - `orderedEntities`: Entities sorted so dependencies come before dependents (for processing order)
 * - `statusCounts`: Computed counts of entities by status (ready, complete, error, skipped)
 * - `clearEntities`: Clears all entities from the database
 * - `getEntityEntryById`: Retrieves an entity entry by its ID
 * - `getDependenciesStatus`: Gets the status of all dependencies for an entity
 * - `addEntity`: Adds or updates a single entity with ready status and optional dependencies
 * - `updateEntityStatus`: Updates the status field for an entity
 *
 * @example
 * ```tsx
 * const {
 *   orderedEntities,
 *   statusCounts,
 *   clearEntities,
 *   addEntity,
 *   updateEntityStatus
 * } = useCopyEntitiesToEnvironmentIndexedDB();
 *
 * // Add an entity with dependencies
 * await addEntity(myEntity, {
 *   targetEnvironment: 'production',
 *   dependencies: [{ id: 'dep-1', domainEntityType: 'config-rule' }]
 * });
 *
 * // Update entity status
 * await updateEntityStatus('entity-id', 'complete');
 *
 * // Process entities in order (dependencies first)
 * for (const entity of orderedEntities ?? []) {
 *   // Dependencies are guaranteed to come before dependents
 *   await processEntity(entity);
 * }
 *
 * // Clear all entities when done
 * await clearEntities();
 * ```
 */
export const useCopyEntitiesToEnvironmentIndexedDB = () => {
  const entities = useLiveQuery(() => db.entities.toArray());

  /**
   * Entities sorted so that dependencies come before dependents.
   * Use this for processing entities in the correct order.
   */
  const orderedEntities = useMemo(
    () => (entities ? topologicalSortEntities(entities) : undefined),
    [entities],
  );

  const statusCounts = useMemo(
    () => ({
      ready:
        entities?.filter((entity) => entity.status === 'ready').length ?? 0,
      complete:
        entities?.filter((entity) => entity.status === 'complete').length ?? 0,
      error:
        entities?.filter((entity) => entity.status === 'error').length ?? 0,
      skipped:
        entities?.filter((entity) => entity.status === 'skipped').length ?? 0,
    }),
    [entities],
  );

  const clearEntities = useCallback(
    async () => db.transaction('rw', db.entities, async () => db.clearAll()),
    [],
  );

  const addEntity = useCallback(
    async (
      entityToAdd: ConfigEntityDetail,
      options?: {
        targetEnvironment?: EnvironmentKey;
        dependencies?: EntityDependencyRef[];
        status?: EntityBulkOperationStatus;
        error?: string;
      },
    ) =>
      db.transaction('rw', db.entities, async () =>
        db.upsertEntity({
          id: entityToAdd.id,
          data: entityToAdd,
          targetEnvironment: options?.targetEnvironment,
          dependencies: options?.dependencies,
          status: options?.status ?? 'ready',
          error: options?.error,
        }),
      ),
    [],
  );

  const updateEntityStatus = useCallback(
    async (id: string, status: EntityBulkOperationStatus, error?: string) =>
      db.transaction('rw', db.entities, async () =>
        db.updateEntityStatusById(id, status, error),
      ),
    [],
  );

  /**
   * Gets counts for all statuses directly from the database.
   * Unlike statusCounts (which is React state), this queries the DB directly
   * and can be used within async callbacks to get the most up-to-date counts.
   */
  const getStatusCounts = useCallback(async () => {
    const [ready, complete, error, skipped] = await Promise.all([
      db.entities.where('status').equals('ready').count(),
      db.entities.where('status').equals('complete').count(),
      db.entities.where('status').equals('error').count(),
      db.entities.where('status').equals('skipped').count(),
    ]);
    return { ready, complete, error, skipped };
  }, []);

  /**
   * Gets all entities with 'error' status directly from the database.
   * Useful for collecting error messages after copying is complete.
   */
  const getErrorEntities = useCallback(
    async () => db.entities.where('status').equals('error').toArray(),
    [],
  );

  /**
   * Gets all entities from the database, topologically sorted so dependencies come first.
   * Unlike orderedEntities (which is React state from useLiveQuery), this queries the DB
   * directly and can be used within async callbacks to get the most up-to-date data.
   *
   * Use this when you need fresh data inside a callback after modifying the database,
   * as useLiveQuery values may be stale due to React closure capture.
   */
  const getOrderedEntities = useCallback(async () => {
    const allEntities = await db.entities.toArray();
    return topologicalSortEntities(allEntities);
  }, []);

  /**
   * Gets the status of all dependencies for a given entity.
   * Returns the status of each dependency and whether all are satisfied.
   *
   * @param entityId - The ID of the entity to check dependencies for
   * @returns A promise resolving to the dependencies status, or null if entity not found
   */
  const getDependenciesStatus = useCallback(
    async (
      entityId: string,
    ): Promise<EntityDependenciesStatusResult | null> => {
      const entity = await db.getEntityEntryById(entityId);
      if (!entity) {
        return null;
      }

      const deps = entity.dependencies ?? [];
      if (deps.length === 0) {
        return {
          dependencies: [],
          allSatisfied: true,
          counts: {
            ready: 0,
            complete: 0,
            error: 0,
            skipped: 0,
          },
        };
      }

      // Fetch all dependency entries in parallel
      const depEntries = await Promise.all(
        deps.map((dep) => db.getEntityEntryById(dep.id)),
      );

      // Build dependency status array
      const dependencyStatuses: EntityDependencyStatus[] = deps.map(
        (dep, index) => {
          const entry = depEntries[index];
          const status = entry?.status;
          return {
            dependency: dep,
            status,
            isSatisfied: status === 'complete' || status === 'skipped',
          };
        },
      );

      // Calculate counts
      const counts = {
        ready: dependencyStatuses.filter((d) => d.status === 'ready').length,
        complete: dependencyStatuses.filter((d) => d.status === 'complete')
          .length,
        error: dependencyStatuses.filter((d) => d.status === 'error').length,
        skipped: dependencyStatuses.filter((d) => d.status === 'skipped')
          .length,
      };

      return {
        dependencies: dependencyStatuses,
        allSatisfied: dependencyStatuses.every((d) => d.isSatisfied),
        counts,
      };
    },
    [],
  );

  return {
    entities,
    orderedEntities,
    clearEntities,
    statusCounts,
    getEntityEntryById,
    getDependenciesStatus,
    addEntity,
    updateEntityStatus,
    getStatusCounts,
    getErrorEntities,
    getOrderedEntities,
  };
};
